import React, { useState, useEffect, useMemo } from 'react';
import { getRLPortfolio, getRLTrades, getRLPerformance, getRLMarketData, startAutonomousTrading, stopAutonomousTrading, getAutonomousTradingStatus, resetRLPortfolio } from '../../api/rlApi';
import './RLPortfolio.css';

function RLPortfolio() {
  const [portfolioData, setPortfolioData] = useState(null);
  const [trades, setTrades] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autonomousStatus, setAutonomousStatus] = useState(null);
  const [autoStatusWarning, setAutoStatusWarning] = useState(null);
  const [tradingActionLoading, setTradingActionLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Load portfolio data
  const loadPortfolioData = async () => {
    try {
      setLoading(true);
      setError(null);
      setAutoStatusWarning(null);

      const withTimeout = (promise, label, ms = 15000) =>
        Promise.race([
          promise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`Timed out loading ${label}`)), ms)
          ),
        ]);

      const withRetry = async (fn, attempts = 2) => {
        let lastErr;
        for (let i = 0; i < attempts; i++) {
          try {
            return await fn();
          } catch (err) {
            lastErr = err;
            if (i === attempts - 1) throw err;
          }
        }
        throw lastErr;
      };

      const [portfolioRes, tradesRes, perfRes, marketRes, autoRes] = await Promise.allSettled([
        withRetry(() => withTimeout(getRLPortfolio(), 'portfolio', 25000), 2),
        withTimeout(getRLTrades(20), 'trades'),
        withTimeout(getRLPerformance(), 'performance'),
        withTimeout(getRLMarketData(), 'market data'),
        withTimeout(getAutonomousTradingStatus(), 'autonomous status', 20000)
      ]);

      if (portfolioRes.status === 'rejected') {
        throw portfolioRes.reason;
      }

      setPortfolioData(portfolioRes.value);
      setTrades(tradesRes.status === 'fulfilled' ? (tradesRes.value.trades || []) : []);
      setPerformance(perfRes.status === 'fulfilled' ? perfRes.value : null);
      setMarketData(marketRes.status === 'fulfilled' ? marketRes.value : null);

      if (autoRes.status === 'fulfilled') {
        setAutonomousStatus(autoRes.value);
      } else {
        setAutonomousStatus(null);
        setAutoStatusWarning(autoRes.reason?.message || 'Unable to load autonomous status right now');
      }

      setLastUpdate(new Date().toLocaleTimeString());

    } catch (err) {
      console.error('Failed to load RL portfolio data:', err);
      const msg =
        err?.message && err.message.includes('401')
          ? "You're not logged in; no portfolio to show. Authenticate first."
          : err?.message || 'Unable to load portfolio right now.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPortfolio = async () => {
    try {
      setResetting(true);
      await resetRLPortfolio();
      await loadPortfolioData();
    } catch (err) {
      console.error('Failed to reset portfolio:', err);
      alert('Failed to reset portfolio: ' + err.message);
    } finally {
      setResetting(false);
    }
  };

  // Handle autonomous trading start/stop
  const handleStartAutonomousTrading = async () => {
    if (!portfolioData?.model_version) {
      alert('No model deployed for autonomous trading');
      return;
    }

    try {
      setTradingActionLoading(true);
      await startAutonomousTrading(portfolioData.model_version);
      // Refresh data after starting
      await loadPortfolioData();
    } catch (err) {
      console.error('Failed to start autonomous trading:', err);
      alert('Failed to start autonomous trading: ' + err.message);
    } finally {
      setTradingActionLoading(false);
    }
  };

  const handleStopAutonomousTrading = async () => {
    try {
      setTradingActionLoading(true);
      await stopAutonomousTrading();
      // Refresh data after stopping
      await loadPortfolioData();
    } catch (err) {
      console.error('Failed to stop autonomous trading:', err);
      alert('Failed to stop autonomous trading: ' + err.message);
    } finally {
      setTradingActionLoading(false);
    }
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    loadPortfolioData();
    const interval = setInterval(loadPortfolioData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  const getStatusColor = (value, thresholds = { good: 0, warning: -5 }) => {
    if (value >= thresholds.good) return '#48bb78';
    if (value >= thresholds.warning) return '#ed8936';
    return '#f56565';
  };

  // Render base UI even if loading/error; show inline states

  const positionsEntries = useMemo(() => {
    if (!portfolioData?.positions) return [];
    return Object.entries(portfolioData.positions).slice(0, 20);
  }, [portfolioData]);

  const marketEntries = useMemo(() => {
    if (!marketData?.symbols) return [];
    return Object.entries(marketData.symbols).slice(0, 20);
  }, [marketData]);

  const modelVersion = portfolioData?.model_version || 'None';
  const truncatedModelVersion =
    modelVersion.length > 48 ? `${modelVersion.slice(0, 48)}…` : modelVersion;

  return (
    <div className="rl-portfolio">
      {error && (
        <div className="rl-portfolio-error">
          <h3>⚠️ Error Loading Portfolio</h3>
          <p>{error}</p>
          <button onClick={loadPortfolioData} className="rl-retry-btn">
            Retry
          </button>
        </div>
      )}

      {loading && !portfolioData && (
        <div className="rl-portfolio-loading">
          <div className="rl-spinner"></div>
          <p>Loading RL Portfolio...</p>
        </div>
      )}
      {/* Header with key metrics */}
      <div className="rl-portfolio-header">
        <div className="rl-metric-card">
          <h3>Portfolio Value</h3>
          <div className="rl-metric-value">
            {formatCurrency(portfolioData?.portfolio_value)}
          </div>
          <div className="rl-metric-change"
               style={{ color: getStatusColor(portfolioData?.total_return_pct) }}>
            {portfolioData?.total_return_pct >= 0 ? '+' : ''}
            {formatPercentage(portfolioData?.total_return_pct)}
          </div>
        </div>

        <div className="rl-metric-card">
          <h3>Available Balance</h3>
          <div className="rl-metric-value">
            {formatCurrency(portfolioData?.current_balance)}
          </div>
        </div>

        <div className="rl-metric-card">
          <h3>Total P&L</h3>
          <div className="rl-metric-value"
               style={{ color: getStatusColor(portfolioData?.total_pnl) }}>
            {formatCurrency(portfolioData?.total_pnl)}
          </div>
        </div>

        <div className="rl-metric-card">
          <h3>Max Drawdown</h3>
          <div className="rl-metric-value"
               style={{ color: getStatusColor(-Math.abs(portfolioData?.max_drawdown || 0), { good: -2, warning: -5 }) }}>
            {formatPercentage(portfolioData?.max_drawdown)}
          </div>
        </div>
      </div>

      {/* Portfolio composition */}
      <div className="rl-portfolio-grid">
        <div className="rl-portfolio-section">
          <h3>📊 Portfolio Overview</h3>
          <div className="rl-overview-grid">
            <div className="rl-overview-item">
              <span className="rl-label">Initial Balance:</span>
              <span className="rl-value">{formatCurrency(portfolioData?.initial_balance)}</span>
            </div>
            <div className="rl-overview-item">
              <span className="rl-label">Positions:</span>
              <span className="rl-value">{portfolioData?.total_positions || 0}</span>
            </div>
            <div className="rl-overview-item">
              <span className="rl-label">Model Version:</span>
              <span className="rl-value rl-truncate" title={modelVersion}>
                {truncatedModelVersion}
              </span>
            </div>
            <div className="rl-overview-item">
              <span className="rl-label">Circuit Breaker:</span>
              <span className={`rl-status ${portfolioData?.circuit_breaker_triggered ? 'triggered' : 'normal'}`}>
                {portfolioData?.circuit_breaker_triggered ? 'TRIGGERED' : 'Normal'}
              </span>
            </div>
          </div>

          {/* Autonomous Trading Controls */}
          <div className="rl-autonomous-controls">
            <h4>🤖 Autonomous Trading</h4>
            <div className="rl-trading-status">
              <span className="rl-label">Status:</span>
              <span className={`rl-status ${autonomousStatus?.is_running ? 'running' : 'stopped'}`}>
                {autonomousStatus?.is_running ? 'RUNNING' : 'STOPPED'}
              </span>
              {autonomousStatus?.is_running && (
                <span className="rl-status-details">
                  (Model: {autonomousStatus.model_id}, Decisions: {autonomousStatus.decisions_made}, Trades: {autonomousStatus.trades_executed})
                </span>
              )}
            {autoStatusWarning && (
              <div className="rl-warning subtle">
                ⚠️ {autoStatusWarning}
              </div>
            )}
            </div>
            <div className="rl-control-buttons">
              {!autonomousStatus?.is_running ? (
                <button
                  onClick={handleStartAutonomousTrading}
                  disabled={tradingActionLoading || !portfolioData?.model_version}
                  className="rl-start-trading-btn"
                >
                  {tradingActionLoading ? '🚀 Starting...' : '🚀 Start Autonomous Trading'}
                </button>
              ) : (
                <button
                  onClick={handleStopAutonomousTrading}
                  disabled={tradingActionLoading}
                  className="rl-stop-trading-btn"
                >
                  {tradingActionLoading ? '⏹️ Stopping...' : '⏹️ Stop Autonomous Trading'}
                </button>
              )}
            <button
              onClick={handleResetPortfolio}
              disabled={resetting}
              className="rl-reset-portfolio-btn"
            >
              {resetting ? '🧹 Resetting...' : '🧹 Reset Portfolio & Trades'}
            </button>
            </div>
            {!portfolioData?.model_version && (
              <p className="rl-warning">⚠️ No model deployed. Deploy a model first to enable autonomous trading.</p>
            )}
          </div>
        </div>

        <div className="rl-portfolio-section">
          <h3>📈 Performance Metrics</h3>
          {performance && (
            <div className="rl-performance-grid">
              <div className="rl-performance-item">
                <span className="rl-label">Total Trades:</span>
                <span className="rl-value">{performance.total_trades || 0}</span>
              </div>
              <div className="rl-performance-item">
                <span className="rl-label">Win Rate:</span>
                <span className="rl-value">{formatPercentage((performance.win_rate || 0) * 100)}</span>
              </div>
              <div className="rl-performance-item">
                <span className="rl-label">Sharpe Ratio:</span>
                <span className="rl-value"
                      style={{ color: getStatusColor(performance.sharpe_ratio, { good: 1, warning: 0.5 }) }}>
                  {performance.sharpe_ratio?.toFixed(2) || 'N/A'}
                </span>
              </div>
              <div className="rl-performance-item">
                <span className="rl-label">Best Trade:</span>
                <span className="rl-value" style={{ color: '#48bb78' }}>
                  {formatPercentage(performance.max_profit_pct)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Current Market Prices */}
        {marketEntries.length > 0 && (
          <div className="rl-portfolio-section rl-market-section">
            <h3>📊 Live Market Prices</h3>
            <div className="rl-market-table">
              <div className="rl-market-header">
                <span>Symbol</span>
                <span>Price</span>
                <span>Volume</span>
                <span>MFI</span>
                <span>VWAP</span>
                <span>Confluence</span>
              </div>
              <div className="rl-market-body">
                {marketEntries.map(([symbol, data]) => (
                  <div key={symbol} className="rl-market-row">
                    <span className="rl-symbol">{symbol.replace('/USDT', '')}</span>
                    <span className="rl-price">${data.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</span>
                    <span className="rl-value">{data.volume?.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    <span className="rl-value" style={{ color: data.indicators?.MFI < 30 ? '#48bb78' : data.indicators?.MFI > 70 ? '#f56565' : '#ed8936' }}>
                      {data.indicators?.MFI?.toFixed(1)}
                    </span>
                    <span className="rl-value">${data.indicators?.VWAP?.toFixed(2)}</span>
                    <span className="rl-value" style={{ color: data.indicators?.GM_Confluence > 0.3 ? '#48bb78' : data.indicators?.GM_Confluence < -0.3 ? '#f56565' : '#ed8936' }}>
                      {data.indicators?.GM_Confluence?.toFixed(3)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rl-market-timestamp">
              Last updated: {marketData.timestamp ? new Date(marketData.timestamp).toLocaleString() : 'Unknown'}
            </div>
          </div>
        )}

      </div>

      {/* Current positions */}
      {positionsEntries.length > 0 && (
        <div className="rl-portfolio-section">
          <h3>💼 Current Positions</h3>
          <div className="rl-positions-table">
            <div className="rl-table-header">
              <span>Symbol</span>
              <span>Amount</span>
              <span>Avg Price</span>
              <span>Current Price</span>
              <span>Unrealized P&L</span>
            </div>
            {positionsEntries.map(([symbol, position]) => (
              <div key={symbol} className="rl-table-row">
                <span className="rl-symbol">{symbol.replace('/USDT', '')}</span>
                <span>{position.amount?.toFixed(6) || '0.000000'}</span>
                <span>{formatCurrency(position.avg_price)}</span>
                <span>{formatCurrency(position.current_price)}</span>
                <span style={{ color: getStatusColor(position.unrealized_pnl) }}>
                  {formatCurrency(position.unrealized_pnl)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RL-Generated Trades */}
      <div className="rl-portfolio-section rl-trades-section">
        <h3>📋 RL-Generated Trades</h3>
        {trades.length > 0 ? (
          <div className="rl-trades-table">
            <div className="rl-table-header">
              <span>Time</span>
              <span>Symbol</span>
              <span>Side</span>
              <span>Amount</span>
              <span>Price</span>
              <span>Model</span>
            </div>
            {trades.slice(0, 10).map((trade, index) => (
              <div key={trade.trade_id || index} className="rl-table-row">
                <span>{new Date(trade.timestamp * 1000).toLocaleString()}</span>
                <span className="rl-symbol">{trade.symbol.replace('/USDT', '')}</span>
                <span className={`rl-trade-side ${trade.side.toLowerCase()}`}>
                  {trade.side.toUpperCase()}
                </span>
                <span>{trade.amount?.toFixed(6) || '0.000000'}</span>
                <span>{formatCurrency(trade.price)}</span>
                <span>{trade.model_version || 'Manual'}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rl-no-trades">
            <p>🤖 No trades generated yet.</p>
            <p>Start autonomous trading to see the RL model generate trades based on market conditions.</p>
            {autonomousStatus?.is_running && (
              <p className="rl-info">Autonomous trading is running - trades will appear here as the model makes decisions.</p>
            )}
          </div>
        )}
      </div>

      {/* RL Agent Decisions */}
      {autonomousStatus?.recent_decisions && autonomousStatus.recent_decisions.length > 0 && (
        <div className="rl-portfolio-section rl-decisions-section">
          <h3>🧠 RL Agent Decisions</h3>
          <div className="rl-decisions-table">
            <div className="rl-table-header">
              <span>Time</span>
              <span>Symbol</span>
              <span>Decision</span>
              <span>Executed</span>
              <span>Model</span>
            </div>
            {[...autonomousStatus.recent_decisions]
              .sort((a, b) => b.timestamp - a.timestamp) // Sort by timestamp descending (newest first)
              .flatMap((decision) =>
              Object.entries(decision.decisions).map(([symbol, decisionData]) => (
                <div key={`${decision.timestamp}-${symbol}`} className="rl-table-row">
                  <span>{new Date(decision.timestamp * 1000).toLocaleString()}</span>
                  <span className="rl-symbol">{symbol.replace('/USDT', '')}</span>
                  <span className={`rl-decision ${decisionData.action}`}>
                    {decisionData.action.toUpperCase()}
                  </span>
                  <span className={`rl-executed ${decisionData.executed ? 'yes' : 'no'}`}>
                    {decisionData.executed ? '✅' : '❌'}
                  </span>
                  <span>{decision.model_id || 'Unknown'}</span>
                </div>
              ))
            )}
          </div>
          <div className="rl-decisions-info">
            <p><strong>Decision Types:</strong> BUY (green), SELL (red), HOLD (gray)</p>
            <p><strong>Execution:</strong> ✅ = Trade executed, ❌ = Decision made but not executed</p>
          </div>
        </div>
      )}

      {/* Footer with last update */}
      <div className="rl-portfolio-footer">
        <button onClick={loadPortfolioData} className="rl-refresh-btn" disabled={loading}>
          {loading ? '🔄 Refreshing...' : '🔄 Refresh'}
        </button>
        <span className="rl-last-update">
          Last updated: {lastUpdate}
        </span>
      </div>
    </div>
  );
}

export default RLPortfolio;